/**
 * Tenant content for user-mepro — ใช้ Configuration Service แบบเดียวกับ user-fortune / user-htd
 * Local dev: VITE_API_BASE_URL ว่าง → relative /api/... ผ่าน Vite proxy (ไม่ยิง localhost:8081)
 */
import { getApiBaseUrl } from '../lib/apiBaseUrl'

export interface TenantContentResponse {
  success: boolean
  data: {
    tenantId: string
    name: string
    description?: string
    frontendTemplate?: string
    branding: { logo: string; favicon: string; [k: string]: unknown }
    content: {
      siteTitle?: string
      marqueeText?: string
      welcomeMessage?: string
      contactInfo?: { line?: string; phone?: string; [k: string]: unknown }
      socialLinks?: unknown
      paymentRates?: {
        type?: 'text' | 'image'
        textContent?: string
        imageUrl?: string
      }
    }
    features?: {
      webChatEnabled?: boolean
      depositEnabled?: boolean
      withdrawEnabled?: boolean
      lotteryEnabled?: boolean
      casinoEnabled?: boolean
      predictionEnabled?: boolean
      redEnvelopeEnabled?: boolean
      luckyWheelEnabled?: boolean
      berThongEnabled?: boolean
      [k: string]: unknown
    }
    updatedAt?: string
  }
}

function getBaseURL(): string {
  return getApiBaseUrl()
}

function normalizeResponse(json: unknown, tenantId: string): TenantContentResponse {
  const data = (json as { data?: unknown }).data ?? json
  const d = data as Record<string, unknown>
  const content = (d.content ?? d) as Record<string, unknown>
  return {
    success: (json as { success?: boolean }).success !== false,
    data: {
      tenantId: (d.tenantId as string) ?? tenantId,
      name: (d.name as string) ?? 'HuayToday',
      description: d.description as string | undefined,
      frontendTemplate:
        typeof d.frontendTemplate === 'string' ? d.frontendTemplate.trim() : undefined,
      branding: (d.branding as TenantContentResponse['data']['branding']) ?? { logo: '', favicon: '' },
      content: {
        siteTitle: (content.siteTitle as string) || undefined,
        marqueeText: (content.marqueeText as string) ?? (d.marqueeText as string),
        welcomeMessage: content.welcomeMessage as string | undefined,
        contactInfo: (content.contactInfo ?? d.contactInfo) as TenantContentResponse['data']['content']['contactInfo'],
        socialLinks: (content.socialLinks ?? d.socialLinks) as unknown,
        paymentRates: content.paymentRates as TenantContentResponse['data']['content']['paymentRates'],
      },
      features: d.features as TenantContentResponse['data']['features'],
      updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
    },
  }
}

export const getTenantContent = async (tenantId: string): Promise<TenantContentResponse> => {
  const baseURL = getBaseURL()
  const headers = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId,
    'X-Tenant-Domain': window.location.hostname,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  }

  try {
    // 1) Configuration Service — endpoint เดียวกับแอดมิน (จัดการเนื้อหา Tenant ที่ /tenant/content)
    // tenantId มาจาก App.tsx → loadTenantTheme() → resolveRuntimeTenantId (local dev: ?tenant= > VITE_* > cache)
    const configUrl = `${baseURL}/api/v1/configuration-service/tenant-content/${tenantId}?_t=${Date.now()}`
    if (import.meta.env.DEV) {
      console.log('📢 [tenantService] GET tenant content (ที่เดียวกับแอดมิน):', {
        url: configUrl,
        tenantId,
      })
    }
    const configRes = await fetch(configUrl, { method: 'GET', headers })

    if (configRes.status === 304) {
      return getFallbackResponse(tenantId)
    }
    if (configRes.ok) {
      const json = await configRes.json()
      const out = normalizeResponse(json, tenantId)
      if (import.meta.env.DEV) {
        const raw = out.data.content.marqueeText ?? ''
        console.log('✅ [tenantService] Tenant content from Configuration Service', {
          tenantId: out.data.tenantId,
          marqueeLength: raw.length,
          marqueeFull: raw,
        })
      }
      return out
    }

    // 2) Fallback ไปที่ tenant-management แบบเดียวกับ user-classic
    if (import.meta.env.DEV) {
      console.warn('Configuration Service failed, trying tenant-management fallback')
    }
    const fallbackUrl = `${baseURL}/api/v1/tenant-management/tenants/${tenantId}/content`
    const fallbackRes = await fetch(fallbackUrl, { method: 'GET', headers })
    if (fallbackRes.ok) {
      const json = await fallbackRes.json()
      const out = normalizeResponse(json, tenantId)
      if (import.meta.env.DEV) {
        console.log('⚠️ Tenant content loaded from tenant-management fallback')
      }
      return out
    }

    throw new Error(`HTTP ${configRes.status}`)
  } catch (e) {
    console.warn('Failed to fetch tenant content:', e)
    return getFallbackResponse(tenantId)
  }
}

function getFallbackResponse(tenantId: string): TenantContentResponse {
  return {
    success: true,
    data: {
      tenantId,
      name: 'HuayToday',
      branding: { logo: '', favicon: '' },
      content: {
        marqueeText:
          'ยินดีต้อนรับทุกท่านเข้าสู่ HuayToday เว็บหวยออนไลน์ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย',
      },
    },
  }
}
